import React, { useEffect, useState } from "react";
import { Location } from "../../models/location";
import { Rep } from "./home";
import WeeklyTimeline from "../../components/ui/WeeklyTimelineChart";
import { WeeklyTimelineDay } from "@/models/weeklyTimeline";
import FilterModal from "../../components/ui/filter";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClipboard } from "@fortawesome/free-solid-svg-icons";
import CopyButton from "../../components/ui/CopyButton";
import { useModal } from "../../components/hooks/useModal";
import { environment } from "../../environments/environment";
import { useAppSelector } from "../../reducers/store";
import { RecentActivityFilter } from "@/models/recentActivityFilter";

interface PhoneNumberProps {
  phone: string;
  index: number;
}

const modalIdentifier = 'teamRecentActivityFilterModal';

const formatPhoneNumber = (number: string) => {
  const cleaned = number.replace(/\D/g, "");
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return `(${match[1]}) ${match[2]}-${match[3]}`;
  }
  return number;
};

export default function TeamRecentActivity(props: {
  selectedLocation: Location;
  allLocations: Location[];
  allAssignedReps: Rep[];
  timelinesWithLocations: {
    location: Location;
    timeline: WeeklyTimelineDay[];
  }[];
}) {
  const selectedSupplier = useAppSelector((state) => state.supplier.selectedSupplier);
  const selectedProducingFacilities = useAppSelector((state) => state.supplier.selectedProducingFacilities);

  const [repsByLo, setRepsByLo] = useState<Rep[]>([]);
  const [searchInput, setSearchInput] = useState<string>("");
  const [filteredReps, setFilteredReps] = useState<Rep[]>([]);
  const [timezone, setTimezone] = useState<string>("EST");
  const [filterTimeline, setFilterTimeline] = useState(false);
  const [isloading, setIsloading] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const [filters, setFilters] = useState({
    qualityAlert: true,
    supplierNote: true,
    dailyNote: true,
    proactiveAudit: true,
    additionalHours: true,
    checkedIn: true,
    dailyProductionVolume: true
  });

  const [open, setOpen, isOpen] = useModal();

  const openTeamPage = () => {
    window.open(`${environment.baseUrl}/team`, "_blank");
  };
  const handleFilterChange = (newFilters: RecentActivityFilter) => {
    setFilters({
      ...newFilters,
      checkedIn: newFilters.checkedIn ?? true
    });
  };

  useEffect(() => {
    if (props.selectedLocation) {
      const allReps: Rep[] =
        props.selectedLocation.agreement?.assignedReps.filter((rep) => rep.deleted == null && (!rep.startTime || (rep.startTime && new Date(rep.startTime) <= new Date())) 
        && (!rep.endTime || (rep.endTime && new Date(rep.endTime) >= new Date()))).map(
          (rep: { representative: Rep }) => (rep.representative as any as Rep),
        ) ?? [];
      setRepsByLo(allReps);
    }
  }, [props.selectedLocation]);

  useEffect(() => {
    const repsToFilter = props.selectedLocation?.id
      ? repsByLo
      : props.allAssignedReps;

    const filtered = repsToFilter.filter((rep) => rep && (rep.deleted == null) && rep.active).filter((rep) =>
      `${rep?.firstName} ${rep?.lastName}`
        .toLowerCase()
        .includes((searchInput ?? "").toLowerCase()),
    );

    setFilteredReps(filtered);
  }, [searchInput, props.selectedLocation, repsByLo, props.allAssignedReps]);

  return (
    <div className="w-[100%] flex-shrink-0 flex-col overflow-visible rounded-[8px] border border-gray-300 bg-[white] p-[8px]">
      <div className="flex flex-row justify-between p-2">
        <div className="flex items-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            className="mr-2 size-7 text-[#212322]"
          >
            <path
              fillRule="evenodd"
              d="M8.25 6.75a3.75 3.75 0 1 1 7.5 0 3.75 3.75 0 0 1-7.5 0ZM15.75 9.75a3 3 0 1 1 6 0 3 3 0 0 1-6 0ZM2.25 9.75a3 3 0 1 1 6 0 3 3 0 0 1-6 0ZM6.31 15.117A6.745 6.745 0 0 1 12 12a6.745 6.745 0 0 1 6.709 7.498.75.75 0 0 1-.372.568A12.696 12.696 0 0 1 12 21.75c-2.305 0-4.47-.612-6.337-1.684a.75.75 0 0 1-.372-.568 6.787 6.787 0 0 1 1.019-4.38Z"
              clipRule="evenodd"
            />
            <path d="M5.082 14.254a8.287 8.287 0 0 0-1.308 5.135 9.687 9.687 0 0 1-1.764-.44l-.115-.04a.563.563 0 0 1-.373-.487l-.01-.121a3.75 3.75 0 0 1 3.57-4.047ZM20.226 19.389a8.287 8.287 0 0 0-1.308-5.135 3.75 3.75 0 0 1 3.57 4.047l-.01.121a.563.563 0 0 1-.373.486l-.115.04c-.567.2-1.156.349-1.764.441Z" />
          </svg>
          <span className="text-[20px] font-semibold text-[#212322]">
            Your Team
          </span>
        </div>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth="1.5"
          stroke="currentColor"
          className="size-6 cursor-pointer text-[#212322]"
          onClick={openTeamPage}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
          />
        </svg>
      </div>
      <div className="border-[1px] border-gray-300"></div>

      <div className="relative mt-[8px] flex w-full items-center">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 16 16"
          fill="currentColor"
          className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 transform text-gray-400"
        >
          <path
            fillRule="evenodd"
            d="M9.965 11.026a5 5 0 1 1 1.06-1.06l2.755 2.754a.75.75 0 1 1-1.06 1.06l-2.755-2.754ZM10.5 7a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0Z"
            clipRule="evenodd"
          />
        </svg>
        <input
          className="w-full rounded-md border border-gray-300 bg-white p-1 pl-[30px] text-sm placeholder-[#8e8e93]"
          type="text"
          placeholder="Search for a team member"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        ></input>
      </div>
      <div className="mt-[8px] h-[calc(40%-105px)]">
        {filteredReps.length === 0 && (
          <div className="flex h-full items-center justify-center">
            <span className="loading loading-spinner loading-lg text-tqsYellow"></span>
          </div>
        )}

        <div className="h-[100%] overflow-y-auto overflow-x-hidden">
          {filteredReps
            .filter(
              (rep, i) =>
                /* remove duplicate reps */ 
                (i === filteredReps.findIndex((r) => r.email === rep?.email))
            )
            .map((rep, index) => (
              <div
                key={index}
                className="flex items-center justify-between overflow-visible p-2"
              >
                {/* Left div with fixed width */}
                <div className="flex w-[120px] items-center">
                  <div
                    className="h-[26px] w-[26px] rounded-full bg-cover bg-center"
                    style={{
                      backgroundImage: `url(${rep.photoAttachment?.downloadURL ? rep.photoAttachment.downloadURL : "/dashboard/avatar.png"})`,
                    }}
                    role="img"
                    aria-label={`${rep.firstName} ${rep.lastName}`}
                  />
                  <span className="ml-2 flex flex-col gap-0 overflow-hidden leading-none">
                    <span className="text-[14px] text-[#212322]">
                      {rep.firstName} <br /> {rep.lastName}
                    </span>
                  </span>
                </div>

                {/* Right div with fixed width */}
                <div className="flex w-[150px] min-w-[150px] items-center justify-between">
                  <a
                    href={`mailto:${rep.email}`}
                    className="flex h-[20px] w-[20px] items-center justify-center rounded-full bg-[#73cffd] text-white"
                    title="Mail"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      className="h-[14px] w-[14px]"
                    >
                      <path d="M1.5 8.67v8.58a3 3 0 0 0 3 3h15a3 3 0 0 0 3-3V8.67l-8.928 5.493a3 3 0 0 1-3.144 0L1.5 8.67Z" />
                      <path d="M22.5 6.908V6.75a3 3 0 0 0-3-3h-15a3 3 0 0 0-3 3v.158l9.714 5.978a1.5 1.5 0 0 0 1.572 0L22.5 6.908Z" />
                    </svg>
                  </a>

                  {rep.cell && (
                    <div className="ml-2 flex items-center text-tqsBlack">
                      <button
                        className="flex h-[20px] w-[20px] items-center justify-center rounded-full bg-[#3a7bfc] text-white"
                        title="Phone"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          className="h-[14px] w-[14px]"
                        >
                          <path
                            fillRule="evenodd"
                            d="M4.848 2.771A49.144 49.144 0 0 1 12 2.25c2.43 0 4.817.178 7.152.52 1.978.292 3.348 2.024 3.348 3.97v6.02c0 1.946-1.37 3.678-3.348 3.97a48.901 48.901 0 0 1-3.476.383.39.39 0 0 0-.297.17l-2.755 4.133a.75.75 0 0 1-1.248 0l-2.755-4.133a.39.39 0 0 0-.297-.17 48.9 48.9 0 0 1-3.476-.384c-1.978-.29-3.348-2.024-3.348-3.97V6.741c0-1.946 1.37-3.68 3.348-3.97ZM6.75 8.25a.75.75 0 0 1 .75-.75h9a.75.75 0 0 1 0 1.5h-9a.75.75 0 0 1-.75-.75Zm.75 2.25a.75.75 0 0 0 0 1.5H12a.75.75 0 0 0 0-1.5H7.5Z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                      <CopyButton text={formatPhoneNumber(rep.cell)} />
                    </div>
                  )}
                </div>
              </div>
            ))}
        </div>
      </div>
      <div className="w-100 flex flex-row items-center p-2">
        <FontAwesomeIcon
          className="mr-[10px] h-[20px] w-[20px] text-[#212322]"
          icon={faClipboard}
        />
        <div className="flex w-full flex-row items-center justify-between">
          <span className="max-h-[30px] max-w-[calc(100%-40px)] overflow-hidden text-ellipsis whitespace-nowrap text-nowrap text-[20px] font-semibold text-[#212322]">
            Recent Activity
          </span>
          <button
            className="flex max-h-[30px] items-center justify-center rounded-md bg-white px-3 py-1 text-[#212322] shadow-md transition duration-300 ease-in-out"
            onClick={() => setOpen(isOpen(modalIdentifier) ? '' : modalIdentifier)}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="mr-2 h-4 w-4 align-middle"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 0 1-.659 1.591l-5.432 5.432a2.25 2.25 0 0 0-.659 1.591v2.927a2.25 2.25 0 0 1-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 0 0-.659-1.591L3.659 7.409A2.25 2.25 0 0 1 3 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0 1 12 3Z"
              />
            </svg>
            <span className="text-sm font-medium text-[#212322]">Filter</span>
          </button>
        </div>
      </div>
      <div className="border border-[1px] border-gray-300"></div>
      <div className="relative mt-0 flex max-h-[calc(60%-36px)] flex-col rounded-[8px] p-[8px] text-tqsBlack">
        {props.timelinesWithLocations &&
        props.timelinesWithLocations.length > 0 ? (
          <WeeklyTimeline
            timelinesWithLocations={props.timelinesWithLocations}
            timezone={timezone}
            allLocations={props.allLocations}
            selectedLocation={props.selectedLocation}
            filters={filters}
          />
        ) : (
          <div
            className="flex h-full w-full items-center justify-center"
            style={{
              paddingTop: `calc(10vh + 50px)`, // Adjust padding based on viewport height
            }}
          >
            <span className="loading loading-spinner loading-lg text-tqsYellow"></span>
          </div>
        )}

        <FilterModal
          isOpen={isOpen(modalIdentifier)}
          onClose={() => setOpen('')}
          filters={filters}
          onFilterChange={handleFilterChange}
        />
      </div>
    </div>
  );
}
